import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DealService } from '@vanilla/services/DealService.js';
import { DealsApiService } from '@vanilla/services/crm/DealsApiService.js';
import { CrmApiRepository } from '@vanilla/services/crm/CrmApiRepository.js';
import { UserService } from '@vanilla/services/UserService.js';
import { PipelineStore } from '@vanilla/constants/Pipelines.js';
import { STAGE_META, getStageLabel } from '@vanilla/constants/Stages.js';
import { isRecurringPaymentModel } from '@vanilla/constants/PaymentModels.js';
import { mapDealContactRoleFromApi } from '@vanilla/services/crm/ApiEntityMapper.js';
import { formatCurrencyShort } from '@vanilla/utils/Utils.js';
import { validateDealForm, getFirstErrorTab } from '@vanilla/ui/deals/DealFormValidator.js';
import { permissions } from '@vanilla/services/PermissionService.js';
import { ActivityService } from '@vanilla/services/ActivityService.js';
import { stageForProbability } from '@vanilla/utils/StageTransitionRules.js';
import { ToastManager } from '@/lib/toast.js';
import { scrollIntoContainer } from '@/utils/scrollIntoContainer.js';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard.js';
import DealFormStepper from './DealFormStepper.jsx';
import DealFormDetailsPanel from './DealFormDetailsPanel.jsx';
import DealFormRevenuePanel from './DealFormRevenuePanel.jsx';
import DealFormContactsPanel from './DealFormContactsPanel.jsx';
import DealFormCommissionFormPanel from './DealFormCommissionFormPanel.jsx';
import DealFormRightSidebar from './DealFormRightSidebar.jsx';
import { isQuickCreateInteraction } from './DealFormSelect.jsx';
import { Badge } from '@/components/ui/badge';
import { FullPageLoader } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from '@/components/ui/dialog';
import { confirmAlert } from '@/lib/confirmAlert';
import { Info, X } from 'lucide-react';
import {
    PANEL_FOR_MODEL,
    todayIsoDate,
    buildRecurringRows,
    createEmptyDealForm,
} from './dealFormUtils.js';

export default function DealFormDialog({ dealId, onClose, onSaved, sidebarTab = null, timelineFilter = null, openTimelineDrawer = false, highlightActivityId = null, initialDeal = null }) {
    const [form, setForm] = useState(() => {
        const base = createEmptyDealForm();
        if (!dealId && initialDeal && typeof initialDeal === 'object') {
            // Merge shallow — only known keys, preserve defaults for missing fields (no regression for normal create)
            const allowed = [
                'dealName','dealClientId','dealOwnerUserId','dealAmount','dealStage','dealProbability',
                'dealType','dealServiceLine','dealPipeline','dealLeadSource','dealCampaignSource',
                'referralContactId','primaryContactId','contactRoles','description','expectedCloseDate',
            ];
            const patch = {};
            for (const k of allowed) if (initialDeal[k] !== undefined && initialDeal[k] !== null && initialDeal[k] !== '') patch[k] = initialDeal[k];
            // Also support snake_case from lead conversion payload
            if (initialDeal.deal_name && !patch.dealName) patch.dealName = initialDeal.deal_name;
            if (initialDeal.client_id && !patch.dealClientId) patch.dealClientId = initialDeal.client_id;
            if (initialDeal.owner_user_id && !patch.dealOwnerUserId) patch.dealOwnerUserId = initialDeal.owner_user_id;
            return { ...base, ...patch };
        }
        return base;
    });
    const [loading, setLoading] = useState(() => Boolean(dealId));
    const skipContactRoleHydrateRef = useRef(Boolean(dealId));
    const [saving, setSaving] = useState(false);
    const [step, setStep] = useState(0);
    const formBodyRef = useRef(null);
    const formLeftColRef = useRef(null);
    const stepperScrollLockRef = useRef(false);
    const [clients, setClients] = useState([]);
    const [users, setUsers] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [allContacts, setAllContacts] = useState([]);
    const [pipelines, setPipelines] = useState([]);
    const [errors, setErrors] = useState({});
    const [banner, setBanner] = useState('');
    const [baseline, setBaseline] = useState('');
    const [staleBadgeText, setStaleBadgeText] = useState(null);
    const [detailsSheetOpen, setDetailsSheetOpen] = useState(false);
    const [isMobileViewport, setIsMobileViewport] = useState(false);
    useEffect(() => {
        const m = window.matchMedia('(max-width: 768px)');
        const onChange = () => setIsMobileViewport(m.matches);
        onChange();
        m.addEventListener('change', onChange);
        return () => m.removeEventListener('change', onChange);
    }, []);

    // Lead conversion pre-fill: merge initialDeal into fresh form (create mode only, no fetch)
    useEffect(() => {
        if (dealId || !initialDeal || typeof initialDeal !== 'object') return;
        setForm((prev) => {
            // Only merge if still pristine (baseline empty means not yet edited)
            const patch = {};
            const map = {
                dealName: initialDeal.dealName ?? initialDeal.deal_name,
                dealClientId: initialDeal.dealClientId ?? initialDeal.client_id,
                dealOwnerUserId: initialDeal.dealOwnerUserId ?? initialDeal.owner_user_id,
                dealLeadSource: initialDeal.dealLeadSource ?? initialDeal.lead_source,
                dealCampaignSource: initialDeal.dealCampaignSource ?? initialDeal.campaign_source,
                referralContactId: initialDeal.referralContactId,
            };
            for (const [k, v] of Object.entries(map)) if (v) patch[k] = v;
            if (!Object.keys(patch).length) return prev;
            return { ...prev, ...patch };
        });
    }, [dealId, initialDeal]);

    useEffect(() => {
        if (!sidebarTab && !timelineFilter && !openTimelineDrawer) return;
        const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
        if (isMobile && (sidebarTab === 'tasks' || sidebarTab === 'nextsteps' || sidebarTab === 'timeline' || timelineFilter || openTimelineDrawer)) {
            setDetailsSheetOpen(true);
        }
    }, [sidebarTab, timelineFilter, openTimelineDrawer]);

    useEffect(() => {
        if (!detailsSheetOpen) return;
        if (sidebarTab !== 'tasks' && sidebarTab !== 'nextsteps' && sidebarTab !== 'timeline') return;
        const t = setTimeout(() => {
            let el = null;
            let card = null;
            if (sidebarTab === 'tasks' || sidebarTab === 'nextsteps') {
                el = document.getElementById(sidebarTab === 'tasks' ? 'sidebarTasksContent' : 'sidebarNextStepsContent');
                card = el?.closest('.sidebar-card');
            } else if (sidebarTab === 'timeline') {
                el = document.getElementById('sidebarTimeline');
                card = el?.closest('.sidebar-card') || document.querySelector('.deal-details-sheet-body .sidebar-card:nth-child(2)') || document.querySelector('#dealRightSidebar .sidebar-card:nth-child(2)');
            }
            if (card) card.classList.remove('collapsed');
            const sheetBody = document.querySelector('.deal-details-sheet-body');
            if (sheetBody && card) {
                sheetBody.scrollTo({ top: Math.max(0, card.offsetTop - 12), behavior: 'smooth' });
            } else if (card) {
                const wrapper = document.getElementById('dealRightSidebar');
                if (wrapper) wrapper.scrollTo({ top: Math.max(0, card.offsetTop - 12), behavior: 'smooth' });
            }
            el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }, 450);
        return () => clearTimeout(t);
    }, [detailsSheetOpen, sidebarTab, timelineFilter, form.id]);

    const isEdit = !!dealId;
    const canAssignOwner = permissions.canAssign('deals');
    const permissionsLoaded = typeof permissions.isLoaded === 'function' ? permissions.isLoaded() : true;
    const canCreateDeal = !permissionsLoaded || permissions.canCreate('deals');
    const canEditDeal = !permissionsLoaded || permissions.canEdit('deals');
    const canDeleteDeal = !permissionsLoaded || permissions.canDelete('deals');
    const hideFinancials = permissionsLoaded && !permissions.canViewFinancials('deals');
    const canSetupDeal = !permissionsLoaded || permissions.canCreate('deals');
    const hideAmount = false;
    const hideProfit = hideFinancials && !canSetupDeal;
    const hideSubmit = permissionsLoaded && (isEdit ? !canEditDeal : !canCreateDeal);

    useEffect(() => {
        if (form.dealStage === 'closedLost') {
            const t = setTimeout(() => {
                const el = document.getElementById('dealLostReason');
                if (el && !form.dealLostReason?.trim()) el.focus();
                else document.getElementById('dealLostReasonCategory')?.focus();
                if (formBodyRef.current) {
                    const grp = document.getElementById('dealLostReasonGroup');
                    if (grp) scrollIntoContainer(grp, { block: 'nearest' });
                }
            }, 150);
            return () => clearTimeout(t);
        }
    }, [form.dealStage]);

    useEffect(() => {
        if (['referral','client_referral','partner_referral','employee_personal_referral'].includes(form.dealLeadSource) && !allContacts.length) {
            CrmApiRepository.getAllContacts().then(list => {
                const arr = Array.isArray(list) ? list : (list?.data || []);
                const mapped = arr.map(c => ({ id: c.id, firstName: c.first_name||c.firstName||'', lastName: c.last_name||c.lastName||'', email: c.email||'' }));
                if (mapped.length) setAllContacts(mapped);
            }).catch(()=>{});
        }
    }, [form.dealLeadSource, allContacts.length]);

    const isDirty = useMemo(() => {
        if (!baseline) return false;
        return JSON.stringify(form) !== baseline;
    }, [form, baseline]);
    const { requestClose } = useUnsavedGuard(isDirty, { message: 'Discard unsaved changes in deal form?' });

    useEffect(() => {
        const onCardTitleClick = (e) => {
            const title = e.target.closest('.sidebar-card-title');
            if (!title) return;
            if (e.target.closest('button')) return;
            const card = title.closest('.sidebar-card');
            if (card) card.classList.toggle('collapsed');
        };
        document.addEventListener('click', onCardTitleClick);
        return () => document.removeEventListener('click', onCardTitleClick);
    }, []);

    useEffect(() => {
        if (loading) return;
        if (dealId && String(form.id || '') !== String(dealId)) return;
        setBaseline(JSON.stringify(form));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    const [mentionUsers, setMentionUsers] = useState([]);
    useEffect(() => {
        const boot = async () => {
            const [c, u, mu, p] = await Promise.all([
                CrmApiRepository.getAllClients().catch(() => []),
                UserService.getAssignableUsers().catch(() => []),
                UserService.getActive().catch(() => UserService.getLookupUsers().catch(() => [])),
                (async () => {
                    await PipelineStore.initialize();
                    return PipelineStore.getAll();
                })(),
            ]);
            setClients(c);
            setUsers(u);
            const allForMentions = Array.isArray(mu) ? mu : [];
            const merged = [...allForMentions];
            const seen = new Set(merged.map((x) => String(x.id)));
            (u || []).forEach((x) => { if (!seen.has(String(x.id))) merged.push(x); });
            setMentionUsers(merged.length ? merged : u);
            setPipelines(p);
        };
        boot();
    }, []);

    useEffect(() => {
        let cancelled = false;
        const updateStaleBadge = async (detail) => {
            const dealKey = form.id || dealId;
            const stage = form.dealStage;
            if (!dealKey || stage === 'closed' || stage === 'closedLost') {
                if (!cancelled) setStaleBadgeText(null);
                return;
            }
            let hasNextSteps = false;
            if (detail && Array.isArray(detail.nextSteps)) {
                hasNextSteps = detail.nextSteps.length > 0;
            } else {
                try {
                    const res = await DealsApiService.getNextSteps(dealKey);
                    const rows = Array.isArray(res) ? res : (res?.data || []);
                    hasNextSteps = rows.length > 0;
                } catch {
                    hasNextSteps = false;
                }
            }
            let activities = [];
            try {
                activities = await ActivityService.getForEntity('deal', dealKey) || [];
            } catch {
                activities = [];
            }
            if (cancelled) return;
            const latest = activities[0]?.createdAt;
            const daysSinceActivity = latest
                ? (Date.now() - new Date(latest).getTime()) / (1000 * 60 * 60 * 24)
                : Infinity;
            const staleActivity = daysSinceActivity >= 14;
            const staleNextStep = !hasNextSteps;
            if (staleActivity && staleNextStep) {
                setStaleBadgeText('Stale — no next step or activity in 14+ days');
            } else {
                setStaleBadgeText(null);
            }
        };
        updateStaleBadge();
        const onChanged = (e) => {
            if (String(e.detail?.dealId) !== String(form.id || dealId)) return;
            updateStaleBadge(e.detail);
        };
        window.addEventListener('dealActivitiesChanged', onChanged);
        return () => {
            cancelled = true;
            window.removeEventListener('dealActivitiesChanged', onChanged);
        };
    }, [form.id, form.dealStage, dealId]);

    useEffect(() => {
        if (!dealId) return;
        skipContactRoleHydrateRef.current = true;
        setBaseline('');
        setLoading(true);
        (async () => {
            try {
                const deal = await DealService.getById(dealId);
                const [splitsRes, rolesRes] = await Promise.all([
                    DealsApiService.getCommissionSplits(dealId).catch(() => null),
                    DealsApiService.getContactRoles(dealId).catch((e) => {
                        console.warn('Failed to load deal contact roles from API:', e);
                        return null;
                    }),
                ]);
                const apiSplits = Array.isArray(splitsRes) ? splitsRes : (splitsRes?.data || []);
                let contactRoles = [];
                if (rolesRes !== null) {
                    const apiRoles = Array.isArray(rolesRes) ? rolesRes : (rolesRes?.data || []);
                    contactRoles = apiRoles
                        .map(mapDealContactRoleFromApi)
                        .filter(Boolean)
                        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                        .map((r) => ({
                            contactId: r.contactId || '',
                            role: r.role || 'business_user',
                            isPrimary: !!r.isPrimary,
                        }));
                } else if (Array.isArray(deal.dealContactRoles) && deal.dealContactRoles.length) {
                    contactRoles = deal.dealContactRoles.map((r) => ({
                        contactId: r.contactId || '',
                        role: r.role || 'business_user',
                        isPrimary: !!r.isPrimary,
                    }));
                }
                const _pm = deal.paymentModel || '';
                const _isRec = isRecurringPaymentModel(_pm);
                const _sched = Array.isArray(deal.customScheduleRows) ? deal.customScheduleRows : [];
                let _recRows = deal.recurringScheduleRows || [];
                let _custRows = _sched;
                const _rawCnt = deal.paymentCount ?? deal.totalInvoices ?? deal.total_invoices ?? deal.payment_count;
                let _cnt = _rawCnt == null || _rawCnt === '' ? 0 : Number(_rawCnt);
                if (!Number.isFinite(_cnt) || _cnt < 0) _cnt = 0;
                let _oneDate = deal.oneTimeDate || deal.expectedFirstInvoiceDate || deal.expected_first_invoice_date || '';
                let _oneAmt = deal.oneTimeAmount || deal.invoiceAmount || deal.invoice_amount || '';
                if (_pm === 'custom') {
                    _recRows = [];
                } else if (_pm === 'milestone' || _pm === 'one_time') {
                    if (_pm === 'one_time' && _sched.length) {
                        _oneDate = _sched[0]?.invoiceDate || _sched[0]?.date || _oneDate;
                        _oneAmt = String(_sched[0]?.amount ?? _oneAmt ?? '');
                    }
                    _recRows = [];
                    _custRows = [];
                } else if (_isRec && _sched.length) {
                    _recRows = _sched.map((r) => ({ date: r.invoiceDate || r.date || '', amount: Number(r.amount) || 0 }));
                    _custRows = [];
                    _cnt = _recRows.length || _cnt;
                }
                if (_isRec && _cnt > 0 && (!_recRows || _recRows.length === 0)) {
                    const _amt = Number(deal.dealAmount ?? deal.amount ?? deal.contractValue ?? deal.contract_value ?? 0) || 0;
                    const _start = deal.expectedCloseDate || deal.dealDate || deal.originalCreatedDate || todayIsoDate();
                    _recRows = buildRecurringRows(_pm, _cnt, _start, _amt, true, [], { recomputeDates: true });
                }
                const _retUsePct = deal.retainerPercent != null && Number(deal.retainerPercent) > 0;
                const _retAmt = _retUsePct ? String(deal.retainerPercent) : (deal.retainerAmount != null ? String(deal.retainerAmount) : '');
                setForm({
                    ...createEmptyDealForm(),
                    id: deal.id,
                    version: deal.version,
                    status: deal.status || 'open',
                    dealName: deal.dealName || deal.name || '',
                    dealDate: deal.dealDate || deal.originalCreatedDate || '',
                    dealClientId: deal.clientId || '',
                    dealOwnerUserId: deal.ownerUserId || '',
                    dealCurrency: deal.dealCurrency || deal.currency || 'USD',
                    dealAmount: (deal.dealAmount ?? deal.amount ?? deal.contractValue ?? deal.contract_value ?? deal.contractValue ?? '') !== '' && (deal.dealAmount ?? deal.amount ?? deal.contractValue ?? deal.contract_value ?? null) != null ? String(deal.dealAmount ?? deal.amount ?? deal.contractValue ?? deal.contract_value) : '',
                    dealStage: deal.dealStage || 'qualification',
                    dealProbability: deal.probability != null ? Number(deal.probability) : (STAGE_META[deal.dealStage]?.probability ?? 0),
                    expectedCloseDate: deal.expectedCloseDate || '',
                    dealType: deal.dealType || '',
                    dealEngagementModel: deal.engagementModel || '',
                    dealServiceLine: deal.serviceLine || '',
                    dealPipeline: deal.dealPipeline || 'digital_product',
                    dealCampaignSource: deal.campaignSource || '',
                    dealLeadSource: deal.leadSource || '',
                    referralContactId: deal.referralContactId || deal.referral_contact_id || '',
                    dealLostReason: deal.lostReason || '',
                    dealLostReasonCategory: deal.lostReasonCategory || '',
                    dealPaymentTerms: deal.paymentTerms || '',
                    dealPaymentModel: _pm,
                    dealPaymentCount: _cnt,
                    taxIncluded: !!deal.taxIncluded,
                    taxRate: deal.taxRate != null ? String(deal.taxRate) : '',
                    taxUsePercent: deal.taxUsePercent ?? true,
                    recurringScheduleRows: _recRows,
                    oneTimeDate: _oneDate,
                    oneTimeAmount: _oneAmt != null ? String(_oneAmt) : '',
                    retainerAmount: _retAmt,
                    retainerUsePercent: _retUsePct,
                    estimatedAnnualValue: deal.estimatedAnnualValue != null ? String(deal.estimatedAnnualValue) : '',
                    ongoingStartDate: deal.ongoingStartDate || deal.revenueStartDate || '',
                    ongoingEndDate: deal.ongoingEndDate || deal.revenueEndDate || '',
                    minCommitment: deal.minCommitment != null ? String(deal.minCommitment) : '',
                    maxBudget: deal.maxBudget != null ? String(deal.maxBudget) : '',
                    ongoingInvoiceFrequency: deal.ongoingInvoiceFrequency || 'monthly',
                    dealMilestones: deal.dealMilestones || [],
                    customScheduleRows: _custRows,
                    contractValue: deal.contractValue != null ? deal.contractValue : (deal.amount || 0),
                    commissionEligible: deal.commissionEligible !== false,
                    commissionModel: deal.commissionModel || 'percentage',
                    commissionRate: deal.commissionRate != null ? String(deal.commissionRate) : '',
                    commissionFixedAmount: deal.commissionFixedAmount != null ? String(deal.commissionFixedAmount) : '',
                    commissionBasis: deal.commissionBasis || deal.commission_basis || 'schedule',
                    payoutMethod: deal.payoutMethod || deal.payout_method || 'per_schedule',
                    commissionSplits: apiSplits.length
                        ? apiSplits
                            .filter((s) => s.userId || s.user_id)
                            .map((s) => ({
                                userId: s.userId || s.user_id,
                                role: (s.role && s.role !== 'contact') ? s.role : 'sales_executive',
                                splitPct: Number(s.splitPct ?? s.split_pct ?? 0),
                            }))
                        : (deal.commissionSplits || []),
                    contactRoles,
                    primaryContactId: deal.primaryContactId || deal.primary_contact_id || null,
                });
                if (deal.ownerUserId) {
                    const assignable = await UserService.getAssignableUsers({
                        includeUserIds: [deal.ownerUserId],
                    }).catch(() => null);
                    if (assignable) setUsers(assignable);
                }
                if (deal.clientId) {
                    const cs = await CrmApiRepository.getAllContacts(deal.clientId).catch(() => []);
                    setContacts(cs);
                }
            } catch (err) {
                console.error('[DealFormDialog] failed to load deal', err);
                setBanner('Failed to load deal.');
            } finally {
                setLoading(false);
            }
        })();
    }, [dealId]);

    useEffect(() => {
        if (!form.dealClientId) {
            setContacts([]);
            return;
        }
        let cancelled = false;
        CrmApiRepository.getAllContacts(form.dealClientId)
            .catch(() => [])
            .then((cs) => {
                if (cancelled) return;
                setContacts(cs);
                if (skipContactRoleHydrateRef.current) {
                    skipContactRoleHydrateRef.current = false;
                    return;
                }
                setForm((prev) => {
                    let rows = (prev.contactRoles || []).filter(
                        (row) => !row.contactId || cs.some((c) => String(c.id) === String(row.contactId)),
                    );
                    const dealPrimaryId = prev.primaryContactId || null;
                    if (!rows.length && cs.length) {
                        rows = cs.map((c) => ({
                            contactId: c.id,
                            role: 'business_user',
                            isPrimary: dealPrimaryId ? String(c.id) === String(dealPrimaryId) : false,
                        }));
                    } else if (dealPrimaryId && rows.length) {
                        rows = rows.map((r) => ({
                            ...r,
                            isPrimary: String(r.contactId) === String(dealPrimaryId),
                        }));
                    }
                    if (JSON.stringify(prev.contactRoles || []) === JSON.stringify(rows)) return prev;
                    return { ...prev, contactRoles: rows };
                });
            });
        return () => { cancelled = true; };
    }, [form.dealClientId]);

    const FIELD_ERROR_KEYS = {
        commissionEligible: 'dealCommissionEligible',
        commissionModel: 'dealCommissionModel',
        commissionRate: 'dealCommissionRate',
        commissionFixedAmount: 'dealCommissionFixed',
        commissionSplits: 'dealCommissionSplit',
        contactRoles: 'dealContactRoles',
        dealMilestones: 'dealMilestoneRows',
        customScheduleRows: 'dealCustomScheduleRows',
        retainerAmount: 'dealRetainerAmount',
        ongoingStartDate: 'dealOngoingStartDate',
        dealPaymentModel: 'dealPaymentModel',
        dealPaymentCount: 'dealPaymentCount',
        dealPaymentTerms: 'dealPaymentTerms',
        dealAmount: ['dealAmount', 'dealContractValue'],
    };

    const clearFieldErrors = useCallback((...keys) => {
        setErrors((prev) => {
            if (!keys.length) return prev;
            let changed = false;
            const next = { ...prev };
            for (const key of keys) {
                const mapped = FIELD_ERROR_KEYS[key] || key;
                const list = Array.isArray(mapped) ? mapped : [mapped];
                if (!list.includes(key)) list.push(key);
                for (const errorKey of list) {
                    if (errorKey in next) {
                        delete next[errorKey];
                        changed = true;
                    }
                }
            }
            return changed ? next : prev;
        });
    }, []);

    const set = (patch) => {
        setForm((prev) => ({ ...prev, ...patch }));
        clearFieldErrors(...Object.keys(patch));
    };
    const setField = (key, value) => set({ [key]: value });

    const focusFirstInvalidField = useCallback((validationErrors) => {
        requestAnimationFrame(() => {
            const errorKeys = Object.keys(validationErrors || {});
            if (!errorKeys.length) return;
            let firstFieldId = null;
            const scope = formLeftColRef.current || formBodyRef.current || document;
            for (const el of scope.querySelectorAll('[data-field]')) {
                const id = el.getAttribute('data-field');
                if (id && validationErrors[id]) {
                    firstFieldId = id;
                    break;
                }
            }
            if (!firstFieldId) firstFieldId = errorKeys[0];
            const group = scope.querySelector(`[data-field="${firstFieldId}"]`)
                || document.querySelector(`[data-field="${firstFieldId}"]`);
            if (group) {
                const root = scrollIntoContainer(group, { block: 'nearest', offset: 16 });
                if (!root) {
                    group.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }
            }
            const input = document.getElementById(firstFieldId)
                || group?.querySelector('input:not([type="hidden"]), select, textarea, [role="combobox"], button');
            if (input && typeof input.focus === 'function') {
                input.focus({ preventScroll: true });
            }
        });
    }, []);

    const isClosedWon = form.dealStage === 'closed';
    const panelForModel = PANEL_FOR_MODEL[form.dealPaymentModel] || '';

    const contractValue = useMemo(() => {
        return Number(form.dealAmount || form.contractValue || 0);
    }, [form.dealAmount, form.contractValue]);

    const taxAmount = useMemo(() => {
        if (!form.taxIncluded) return 0;
        const raw = Number(form.taxRate) || 0;
        if (form.taxUsePercent === false) return Math.round(raw * 100) / 100;
        return Math.round(contractValue * raw / 100 * 100) / 100;
    }, [form.taxIncluded, form.taxRate, form.taxUsePercent, contractValue]);

    const totalWithTax = contractValue + taxAmount;

    const commissionAssignedPct = useMemo(() => {
        return (form.commissionSplits || []).reduce((sum, s) => sum + (Number(s.splitPct) || 0), 0);
    }, [form.commissionSplits]);

    const commissionEstimate = useMemo(() => {
        if (form.commissionEligible === false) return 0;
        const base = contractValue;
        if (form.commissionModel === 'fixed_amount') {
            const pool = Number(form.commissionFixedAmount) || 0;
            return Math.round((pool * Math.min(commissionAssignedPct, 100)) / 100);
        }
        const rate = Number(form.commissionRate) || 0;
        return Math.round((base * rate * Math.min(commissionAssignedPct, 100)) / 10000);
    }, [form.commissionEligible, form.commissionModel, form.commissionFixedAmount, form.commissionRate, contractValue, commissionAssignedPct]);

    const handleStageChange = (stage) => {
        const min = STAGE_META[stage]?.probabilityMin ?? 0;
        set({
            dealStage: stage,
            dealProbability: min,
        });
    };

    /** Vanilla onProbabilityChange({ syncStage: true }) — map slider to stage on commit. */
    const handleProbabilityCommit = (raw) => {
        const v = Math.min(100, Math.max(0, Number(raw) || 0));
        setForm((prev) => {
            let mapped = stageForProbability(v);
            if (v === 0 && prev.dealStage === 'closedLost') {
                mapped = 'closedLost';
            }
            if (mapped && mapped !== prev.dealStage) {
                return { ...prev, dealProbability: v, dealStage: mapped };
            }
            return { ...prev, dealProbability: v };
        });
        clearFieldErrors('dealProbability', 'dealStage');
    };

    const handlePaymentModelChange = (model) => {
        setForm((prev) => {
            const next = { ...prev, dealPaymentModel: model };
            if (isRecurringPaymentModel(model)) {
                const count = Math.max(0, parseInt(prev.dealPaymentCount, 10) || 0);
                next.recurringScheduleRows = count > 0
                    ? buildRecurringRows(
                        model,
                        count,
                        prev.expectedCloseDate || prev.dealDate || todayIsoDate(),
                        Number(prev.dealAmount) || 0,
                        prev.divideEqually,
                        prev.recurringScheduleRows || [],
                        { recomputeDates: true },
                    )
                    : [];
            }
            return next;
        });
        clearFieldErrors('dealPaymentModel');
    };

    const handlePaymentCountChange = (raw) => {
        let val = parseInt(raw, 10);
        if (raw === '' || Number.isNaN(val) || val < 0) {
            set({ dealPaymentCount: raw === '' ? '' : raw, recurringScheduleRows: [] });
            return;
        }
        if (val === 0) {
            setForm((prev) => ({ ...prev, dealPaymentCount: 0, recurringScheduleRows: [] }));
            clearFieldErrors('dealPaymentCount');
            return;
        }
        setForm((prev) => {
            const rows = isRecurringPaymentModel(prev.dealPaymentModel)
                ? buildRecurringRows(
                    prev.dealPaymentModel,
                    val,
                    prev.expectedCloseDate || prev.dealDate || todayIsoDate(),
                    Number(prev.dealAmount) || 0,
                    prev.divideEqually,
                    prev.recurringScheduleRows || [],
                    { recomputeDates: false },
                )
                : prev.recurringScheduleRows;
            return { ...prev, dealPaymentCount: val, recurringScheduleRows: rows };
        });
        clearFieldErrors('dealPaymentCount');
    };

    const handleDivideEquallyChange = (v) => {
        setForm((prev) => {
            const rows = isRecurringPaymentModel(prev.dealPaymentModel)
                ? buildRecurringRows(
                    prev.dealPaymentModel,
                    prev.dealPaymentCount,
                    prev.expectedCloseDate || prev.dealDate || todayIsoDate(),
                    Number(prev.dealAmount) || 0,
                    v,
                    prev.recurringScheduleRows || [],
                    { recomputeDates: false },
                )
                : prev.recurringScheduleRows;
            return { ...prev, divideEqually: v, recurringScheduleRows: rows };
        });
    };

    useEffect(() => {
        if (loading) return;
        if (form.id) return;
        if (!isRecurringPaymentModel(form.dealPaymentModel)) return;
        const cnt = Math.max(0, parseInt(form.dealPaymentCount, 10) || 0);
        if (cnt < 1) return;
        if ((form.recurringScheduleRows || []).length > 0) return;
        const amt = Number(form.dealAmount) || 0;
        const start = form.expectedCloseDate || form.dealDate || todayIsoDate();
        const rows = buildRecurringRows(form.dealPaymentModel, cnt, start, amt, !!form.divideEqually, [], { recomputeDates: true });
        if (rows.length) setForm((prev) => ({ ...prev, recurringScheduleRows: rows }));
    }, [loading, form.dealPaymentModel, form.dealPaymentCount, form.dealAmount, form.expectedCloseDate, form.divideEqually, (form.recurringScheduleRows || []).length]);

    const addMilestone = () => {
        const rows = [...(form.dealMilestones || [])];
        rows.push({ title: `Milestone ${rows.length + 1}`, dueDate: '', amount: '' });
        set({ dealMilestones: rows });
    };

    const updateMilestone = (i, patch) => {
        const rows = [...(form.dealMilestones || [])];
        rows[i] = { ...rows[i], ...patch };
        set({ dealMilestones: rows });
    };

    const removeMilestone = (i) => {
        const rows = [...(form.dealMilestones || [])];
        rows.splice(i, 1);
        set({ dealMilestones: rows });
    };

    const addCustomRow = () => {
        set({ customScheduleRows: [...(form.customScheduleRows || []), { invoiceDate: '', amount: '', notes: '' }] });
    };

    const updateCustomRow = (i, patch) => {
        const rows = [...(form.customScheduleRows || [])];
        rows[i] = { ...rows[i], ...patch };
        set({ customScheduleRows: rows });
    };

    const removeCustomRow = (i) => {
        const rows = [...(form.customScheduleRows || [])];
        rows.splice(i, 1);
        set({ customScheduleRows: rows });
    };

    const addSplit = () => {
        set({ commissionSplits: [...(form.commissionSplits || []), { userId: '', role: 'sales_executive', splitPct: 0 }] });
    };

    const updateSplit = (i, patch) => {
        const rows = [...(form.commissionSplits || [])];
        rows[i] = { ...rows[i], ...patch };
        set({ commissionSplits: rows });
    };

    const removeSplit = (i) => {
        const rows = [...(form.commissionSplits || [])];
        rows.splice(i, 1);
        set({ commissionSplits: rows });
    };

    const splitEvenly = () => {
        const recipients = (form.commissionSplits || []).filter((s) => s.userId);
        if (!recipients.length) return;
        const each = Math.floor(100 / recipients.length);
        set({
            commissionSplits: recipients.map((r, i) => ({
                ...r,
                splitPct: i === recipients.length - 1 ? 100 - each * (recipients.length - 1) : each,
            })),
        });
    };

    const addContactRole = () => {
        if (!contacts.length) return;
        const rows = form.contactRoles || [];
        const usedIds = new Set(rows.map((r) => r.contactId).filter(Boolean));
        const nextContact = contacts.find((c) => !usedIds.has(c.id));
        set({
            contactRoles: [
                ...rows,
                {
                    contactId: nextContact?.id || '',
                    role: 'business_user',
                    isPrimary: rows.length === 0,
                },
            ],
        });
    };

    const updateContactRole = (i, patch) => {
        const rows = [...(form.contactRoles || [])];
        rows[i] = { ...rows[i], ...patch };
        if (patch.isPrimary) {
            rows.forEach((r, idx) => { if (idx !== i) r.isPrimary = false; });
        }
        const primaryId = rows.find((r) => r.isPrimary)?.contactId || null;
        set({ contactRoles: rows, primaryContactId: primaryId });
    };

    const removeContactRole = (i) => {
        const rows = [...(form.contactRoles || [])];
        rows.splice(i, 1);
        set({ contactRoles: rows });
    };

    const validate = () => {
        const contactRoles = form.contactRoles || [];
        const primaryContactId = contactRoles.find((r) => r.isPrimary)?.contactId || null;
        const result = validateDealForm({
            dealName: form.dealName,
            clientId: form.dealClientId,
            ownerUserId: form.dealOwnerUserId,
            pipeline: form.dealPipeline,
            stage: form.dealStage,
            dealAmount: form.dealAmount,
            probability: form.dealProbability,
            expectedCloseDate: form.expectedCloseDate,
            lostReason: form.dealLostReason,
            lostReasonCategory: form.dealLostReasonCategory,
            dealLostReasonCategory: form.dealLostReasonCategory,
            leadSource: form.dealLeadSource,
            referralContactId: form.referralContactId || '',
            dealType: form.dealType,
            serviceLine: form.dealServiceLine,
            primaryContactId,
            clientContactIds: contacts.map((c) => c.id),
            dealContactRoles: contactRoles,
            contractValue,
            paymentModel: form.dealPaymentModel,
            paymentTerms: form.dealPaymentTerms,
            paymentCount: form.dealPaymentCount,
            commissionEligible: form.commissionEligible,
            commissionSplits: form.commissionSplits || [],
            dealMilestones: form.dealMilestones || [],
            customScheduleRows: form.customScheduleRows || [],
            currency: form.dealCurrency,
            taxIncluded: form.taxIncluded,
        });
        setErrors(result.errors || {});
        if (Object.keys(result.errors || {}).length) {
            const tab = getFirstErrorTab(result.errors);
            const tabIndex = visibleSteps.findIndex((s) => s.key === tab);
            if (tabIndex >= 0) {
                setStep(tabIndex);
                const key = visibleSteps[tabIndex]?.key;
                const panelScope = formLeftColRef.current || formBodyRef.current;
                const target = key && panelScope
                    ? panelScope.querySelector(`[data-stepper-panel="${key}"]`)
                    : null;
                if (target) {
                    stepperScrollLockRef.current = true;
                    const scrolledRoot = scrollIntoContainer(target, { block: 'start', offset: 16 });
                    if (!scrolledRoot) {
                        target.scrollIntoView({ block: 'start', behavior: 'smooth' });
                    }
                    window.setTimeout(() => { stepperScrollLockRef.current = false; }, 450);
                }
            }
            window.setTimeout(() => focusFirstInvalidField(result.errors), 120);
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        if (saving || hideSubmit) return;
        if (isEdit ? !permissions.canEdit('deals') : !permissions.canCreate('deals')) {
            ToastManager.error(
                'Permission denied',
                isEdit ? 'You do not have permission to edit deals.' : 'You do not have permission to create deals.',
            );
            return;
        }
        if (!validate()) return;
        setSaving(true);
        setBanner('');
        try {
            const contactRolesRaw = form.contactRoles || [];
            const primaryContactId = contactRolesRaw.find((r) => r.isPrimary)?.contactId || null;
            const contactIds = contactRolesRaw.map((r) => r.contactId).filter(Boolean);
            const dealContactRoles = contactRolesRaw
                .filter((r) => r.contactId)
                .map((r, i) => ({
                    contactId: r.contactId,
                    role: r.role || 'business_user',
                    isPrimary: primaryContactId ? r.contactId === primaryContactId : !!r.isPrimary,
                    sortOrder: i,
                }));

            const deal = {
                id: form.id || undefined,
                version: form.version,
                status: form.status,
                dealName: form.dealName,
                dealDate: form.dealDate,
                clientId: form.dealClientId,
                ownerUserId: form.dealOwnerUserId,
                dealCurrency: form.dealCurrency,
                amount: Number(form.dealAmount) || 0,
                contractValue,
                dealStage: form.dealStage,
                probability: Number(form.dealProbability) || 0,
                expectedCloseDate: form.expectedCloseDate,
                dealType: form.dealType,
                engagementModel: form.dealEngagementModel || null,
                serviceLine: form.dealServiceLine,
                dealPipeline: form.dealPipeline || 'digital_product',
                campaignSource: form.dealCampaignSource,
                leadSource: form.dealLeadSource,
                referralName: form.referralName,
                referralEmail: form.referralEmail,
                lostReason: form.dealLostReason,
                lostReasonCategory: form.dealLostReasonCategory,
                referralContactId: form.referralContactId,
                paymentTerms: form.dealPaymentTerms,
                paymentModel: form.dealPaymentModel,
                paymentCount: form.dealPaymentModel === 'one_time' ? 1 : (Number(form.dealPaymentCount) || 0),
                totalInvoices: form.dealPaymentModel === 'one_time' ? 1 : (Number(form.dealPaymentCount) || 0),
                invoiceFrequency: isRecurringPaymentModel(form.dealPaymentModel)
                    ? { monthly: 'monthly', weekly: 'every:1:weeks', quarterly: 'quarterly', annual: 'yearly' }[form.dealPaymentModel]
                    : 'monthly',
                taxIncluded: form.taxIncluded,
                taxRate: Number(form.taxRate) || 0,
                recurringScheduleRows: panelForModel === 'recurring' ? (form.recurringScheduleRows || []) : [],
                oneTimeDate: panelForModel === 'oneTime' ? form.oneTimeDate : '',
                oneTimeAmount: panelForModel === 'oneTime' ? Number(form.oneTimeAmount) || 0 : 0,
                ongoingStartDate: panelForModel === 'ongoing' ? form.ongoingStartDate : '',
                ongoingEndDate: panelForModel === 'ongoing' ? form.ongoingEndDate : '',
                retainerAmount: panelForModel === 'ongoing' ? (() => {
                    const raw = Number(form.retainerAmount) || 0;
                    if (form.retainerUsePercent) {
                        const base = Number(form.dealAmount) || 0;
                        return base > 0 && raw > 0 ? Math.round(base * raw / 100 * 100) / 100 : 0;
                    }
                    return raw;
                })() : null,
                retainerPercent: panelForModel === 'ongoing' && form.retainerUsePercent ? (Number(form.retainerAmount) || 0) : null,
                estimatedAnnualValue: panelForModel === 'ongoing' ? (Number(form.estimatedAnnualValue) || 0) : null,
                minCommitment: panelForModel === 'ongoing' ? (Number(form.minCommitment) || 0) : null,
                maxBudget: panelForModel === 'ongoing' ? (Number(form.maxBudget) || 0) : null,
                ongoingInvoiceFrequency: panelForModel === 'ongoing' ? form.ongoingInvoiceFrequency : '',
                dealMilestones: panelForModel === 'milestone' ? (form.dealMilestones || []) : [],
                customScheduleRows: panelForModel === 'custom' ? (form.customScheduleRows || []) : [],
                commissionEligible: form.commissionEligible,
                commissionModel: form.commissionModel,
                commissionRate: form.commissionRate,
                commissionFixedAmount: form.commissionFixedAmount,
                commissionBasis: form.commissionBasis || 'schedule',
                commission_basis: form.commissionBasis || 'schedule',
                payoutMethod: form.payoutMethod || 'per_schedule',
                payout_method: form.payoutMethod || 'per_schedule',
                commissionSplits: (form.commissionSplits || []).filter((s) => s.userId && Number(s.splitPct) > 0),
                dealContactRoles,
                primaryContactId,
                primary_contact_id: primaryContactId,
            };

            await DealService.save(deal, contactIds, primaryContactId);
            onSaved();
            onClose();
        } catch (err) {
            console.error('[DealFormDialog] save failed', err);
            setBanner(err?.message || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!form.id) return;
        if (!permissions.canDelete('deals')) {
            ToastManager.error('Permission denied', 'You do not have permission to delete deals.');
            return;
        }
        const ok = await confirmAlert({
            title: 'Delete deal',
            message: 'Delete this deal? It can be restored by an administrator.',
            confirmLabel: 'Delete',
            confirmClass: 'btn-danger',
        });
        if (!ok) return;
        try {
            await DealService.delete(form.id);
            onSaved();
            onClose();
        } catch (err) {
            console.error('[DealFormDialog] delete failed', err);
            setBanner(err?.message || 'Delete failed.');
        }
    };

    const milestoneScheduled = (form.dealMilestones || []).reduce((s, m) => s + (Number(m.amount) || 0), 0);
    const milestoneRemaining = contractValue - milestoneScheduled;
    const customScheduled = (form.customScheduleRows || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const customRemaining = contractValue - customScheduled;

    const pipelineOptions = pipelines.length ? pipelines : [{ id: 'digital_product', name: 'Digital Product' }];

    const recipientUsers = users;

    const visibleSteps = useMemo(() => {
        const steps = [{ key: 'details', label: 'Details' }];
        if (!hideAmount) steps.push({ key: 'revenue', label: 'Revenue & Billing' });
        steps.push({ key: 'contacts', label: 'Contacts' });
        if (!hideProfit) steps.push({ key: 'commission', label: 'Commission' });
        return steps;
    }, [hideAmount, hideProfit]);

    const visibleStepsRef = useRef(visibleSteps);
    visibleStepsRef.current = visibleSteps;

    useEffect(() => {
        if (step > visibleSteps.length - 1) setStep(Math.max(0, visibleSteps.length - 1));
    }, [step, visibleSteps.length]);

    const getScrollRoot = useCallback(() => {
        const left = formLeftColRef.current;
        const body = formBodyRef.current;
        if (left && typeof window !== 'undefined') {
            const oy = window.getComputedStyle(left).overflowY;
            if ((oy === 'auto' || oy === 'scroll' || oy === 'overlay') && left.scrollHeight > left.clientHeight + 1) {
                return left;
            }
        }
        if (body && typeof window !== 'undefined') {
            const oy = window.getComputedStyle(body).overflowY;
            if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') return body;
        }
        if (left && typeof window !== 'undefined') {
            const oy = window.getComputedStyle(left).overflowY;
            if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') return left;
        }
        return left || body;
    }, []);

    const goToStep = (index, { scroll = true } = {}) => {
        const i = Math.max(0, Math.min(index, visibleSteps.length - 1));
        setStep(i);
        if (!scroll) return;
        const key = visibleSteps[i]?.key;
        const panelScope = formLeftColRef.current || formBodyRef.current;
        const target = key && panelScope
            ? panelScope.querySelector(`[data-stepper-panel="${key}"]`)
            : null;
        if (!target) return;
        stepperScrollLockRef.current = true;
        const scrolledRoot = scrollIntoContainer(target, { block: 'start', offset: 16 });
        if (!scrolledRoot) {
            const scrollRoot = getScrollRoot();
            if (scrollRoot) {
                const rootRect = scrollRoot.getBoundingClientRect();
                const targetRect = target.getBoundingClientRect();
                scrollRoot.scrollTop += targetRect.top - rootRect.top - 16;
            } else {
                target.scrollIntoView({ block: 'start', behavior: 'smooth' });
            }
        }
        window.setTimeout(() => {
            stepperScrollLockRef.current = false;
        }, 450);
    };

    const syncStepFromScroll = useCallback((event) => {
        if (stepperScrollLockRef.current) return;
        const root = event?.currentTarget || getScrollRoot();
        if (!root) return;

        const steps = visibleStepsRef.current;
        const sectionEls = steps
            .map((s) => root.querySelector(`[data-stepper-panel="${s.key}"]`))
            .filter(Boolean);
        if (!sectionEls.length) return;

        const marker = root.getBoundingClientRect().top + 96;
        let activeIdx = 0;
        for (let i = 0; i < sectionEls.length; i++) {
            if (sectionEls[i].getBoundingClientRect().top <= marker) activeIdx = i;
        }
        setStep((prev) => (prev === activeIdx ? prev : activeIdx));
    }, [getScrollRoot]);


    const sidebarProps = {
        form,
        users,
        mentionUsers,
        sidebarTab,
        timelineFilter,
        openTimelineDrawer,
        highlightActivityId,
        contractValue,
        taxAmount,
        totalWithTax,
        commissionEstimate,
        commissionAssignedPct,
    };


    const footerMeta = isEdit
        ? `Last updated ${form.updatedAt ? new Date(form.updatedAt).toLocaleDateString() : '—'}`
        : 'Not saved yet';

    return (
        <>
            <Dialog
                open
                onOpenChange={(next) => { if (!next) requestClose(onClose); }}
            >
                <DialogContent
                    unstyled
                    hideClose
                    id="dealModal"
                    overlayClassName="modal active deal-modal-overlay"
                    className="modal-content modal-deal"
                    aria-labelledby="dealModalTitle"
                    onEscapeKeyDown={(e) => {
                        if (isQuickCreateInteraction(e.target) || document.querySelector('.quick-create-popup.active')) {
                            e.preventDefault();
                            return;
                        }
                        e.preventDefault();
                        requestClose(onClose);
                    }}
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onFocusOutside={(e) => e.preventDefault()}
                    onInteractOutside={(e) => e.preventDefault()}
                >
                    {loading && (
                        <FullPageLoader message="Loading deal..." />
                    )}
                    <form id="dealForm" className={`deal-form${hideSubmit ? ' is-readonly' : ''}`} onSubmit={handleSubmit}>
                        <header className="deal-form-header">
                            <div className="deal-form-header-left">
                                <span className="deal-form-header-icon" aria-hidden="true">
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.4" /><path d="M7 13V8.5l2.5-1.5L12 8.5V13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </span>
                                <DialogTitle unstyled id="dealModalTitle" className="deal-form-title">{isEdit ? 'Edit Deal' : 'Create Deal'}</DialogTitle>
                                {form.dealStage && (
                                    <Badge
                                        id="dealStageBadge"
                                        variant="outline"
                                        className="deal-stage-badge border-0 text-inherit shadow-none hover:bg-transparent"
                                        style={{ backgroundColor: STAGE_META[form.dealStage]?.color }}
                                    >
                                        {getStageLabel(form.dealStage)}
                                    </Badge>
                                )}
                                <span id="dealForecastTotal" className="deal-forecast-total">{formatCurrencyShort(Math.round((contractValue * (Number(form.dealProbability) || 0)) / 100))} forecast</span>
                                {staleBadgeText ? (
                                    <Badge id="dealStaleBadge" variant="warning" className="deal-stale-badge">{staleBadgeText}</Badge>
                                ) : (
                                    <Badge id="dealStaleBadge" variant="warning" className="deal-stale-badge hidden">Needs attention</Badge>
                                )}
                            </div>
                            <button type="button" className="modal-close" aria-label="Close" onClick={() => requestClose(onClose)}>&times;</button>
                        </header>

                        <DealFormStepper visibleSteps={visibleSteps} step={step} goToStep={goToStep} />

                        <div className="flex md:hidden items-center justify-between px-3 py-2 border-y border-border bg-muted/20 shrink-0 relative z-10">
                            <span className="text-xs font-medium text-muted-foreground">Deal Details</span>
                            <div className="flex items-center gap-2">
                                <Button type="button" variant="outline" size="sm" className="h-8 px-3 text-xs gap-1.5 relative z-10" onClick={() => setDetailsSheetOpen(true)} id="dealDetailsSheetTrigger">
                                    <Info className="size-3.5" /> Details
                                </Button>
                            </div>
                        </div>

                        <div
                            className="deal-form-body is-premium-layout"
                            ref={formBodyRef}
                            onScroll={syncStepFromScroll}
                        >
                            <div className="deal-form-left-column" ref={formLeftColRef} onScroll={syncStepFromScroll}>
                                {visibleSteps.some((s) => s.key === 'details') && (
                                    <DealFormDetailsPanel
                                        form={form}
                                        errors={errors}
                                        setField={setField}
                                        setClients={setClients}
                                        setUsers={setUsers}
                                        setContacts={setContacts}
                                        setAllContacts={setAllContacts}
                                        setPipelines={setPipelines}
                                        clients={clients}
                                        users={users}
                                        contacts={contacts}
                                        allContacts={allContacts}
                                        pipelineOptions={pipelineOptions}
                                        canAssignOwner={canAssignOwner}
                                        handleStageChange={handleStageChange}
                                        handleProbabilityCommit={handleProbabilityCommit}
                                    />
                                )}
                                {visibleSteps.some((s) => s.key === 'revenue') && (
                                    <DealFormRevenuePanel
                                        form={form}
                                        errors={errors}
                                        set={set}
                                        setField={setField}
                                        contractValue={contractValue}
                                        isClosedWon={isClosedWon}
                                        panelForModel={panelForModel}
                                        taxAmount={taxAmount}
                                        totalWithTax={totalWithTax}
                                        milestoneScheduled={milestoneScheduled}
                                        milestoneRemaining={milestoneRemaining}
                                        customScheduled={customScheduled}
                                        customRemaining={customRemaining}
                                        handlePaymentModelChange={handlePaymentModelChange}
                                        handlePaymentCountChange={handlePaymentCountChange}
                                        handleDivideEquallyChange={handleDivideEquallyChange}
                                        addMilestone={addMilestone}
                                        updateMilestone={updateMilestone}
                                        removeMilestone={removeMilestone}
                                        addCustomRow={addCustomRow}
                                        updateCustomRow={updateCustomRow}
                                        removeCustomRow={removeCustomRow}
                                    />
                                )}
                                {visibleSteps.some((s) => s.key === 'contacts') && (
                                    <DealFormContactsPanel
                                        form={form}
                                        contacts={contacts}
                                        addContactRole={addContactRole}
                                        updateContactRole={updateContactRole}
                                        removeContactRole={removeContactRole}
                                    />
                                )}
                                {visibleSteps.some((s) => s.key === 'commission') && (
                                    <DealFormCommissionFormPanel
                                        form={form}
                                        setField={setField}
                                        users={users}
                                        recipientUsers={recipientUsers}
                                        commissionEstimate={commissionEstimate}
                                        commissionAssignedPct={commissionAssignedPct}
                                        splitEvenly={splitEvenly}
                                        addSplit={addSplit}
                                        updateSplit={updateSplit}
                                        removeSplit={removeSplit}
                                    />
                                )}
                            </div>
                            {!isMobileViewport && (
                                <div className="hidden md:contents">
                                    <DealFormRightSidebar {...sidebarProps} />
                                </div>
                            )}
                        </div>

                        <footer className="deal-form-footer">
                            {banner && <div id="dealFormBanner" className="deal-form-banner" role="alert">{banner}</div>}
                            <div className="deal-form-footer-row">
                                <div className="deal-form-footer-left">
                                    {isEdit && canDeleteDeal && (
                                        <button type="button" className="deal-delete-btn" id="dealDeleteBtn" aria-label="Delete deal" onClick={handleDelete}>
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 4h12M5 4V2.5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1V4M3 4v9a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4M6 7v5M10 7v5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /><path d="M2 4h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                                        </button>
                                    )}
                                </div>
                                <p id="dealFormMeta" className="deal-form-meta">{footerMeta}</p>
                                <div className="deal-form-actions">
                                    <Button type="button" className="btn btn-secondary" variant="secondary" id="cancelBtn" onClick={() => requestClose(onClose)} disabled={saving}>Cancel</Button>
                                    {step > 0 && (
                                        <Button type="button" className="btn btn-secondary" variant="secondary" id="dealPrevBtn" onClick={() => goToStep(step - 1)} disabled={saving}>← Back</Button>
                                    )}
                                    {step < visibleSteps.length - 1 && (
                                        <Button type="button" className="btn btn-secondary" variant="secondary" id="dealNextBtn" onClick={() => goToStep(step + 1)} disabled={saving}>Next →</Button>
                                    )}
                                    {!hideSubmit && (
                                        <Button
                                            type="button"
                                            className="btn btn-primary"
                                            variant="default"
                                            id="dealSubmitBtn"
                                            disabled={saving}
                                            onClick={handleSubmit}
                                        >
                                            {saving ? 'Saving...' : (isEdit ? (
                                                <>Save<span className="deal-btn-label-suffix"> Deal</span></>
                                            ) : (
                                                <>Create<span className="deal-btn-label-suffix"> Deal</span></>
                                            ))}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </footer>
                    </form>
                    {detailsSheetOpen && typeof document !== 'undefined' && (
                        <div className="fixed inset-0 z-[1002] flex flex-col justify-end" role="dialog" aria-modal="true" aria-labelledby="dealDetailsSheetTitle" data-deal-details-sheet>
                            <div className="absolute inset-0 bg-black/40" onClick={() => setDetailsSheetOpen(false)} aria-hidden="true" />
                            <div className="relative bg-background rounded-t-2xl border-t shadow-2xl flex flex-col h-[85vh] max-h-[88vh] w-full animate-in slide-in-from-bottom duration-300">
                                <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full mx-auto mt-2.5 mb-1 shrink-0" />
                                <div className="px-5 py-4 border-b flex items-center justify-between shrink-0">
                                    <div>
                                        <h2 id="dealDetailsSheetTitle" className="text-base font-bold text-foreground">Deal Details</h2>
                                        <p className="text-xs text-muted-foreground mt-0.5">Activities, revenue and commission summary</p>
                                    </div>
                                    <button type="button" className="rounded-sm opacity-70 hover:opacity-100 p-1" onClick={() => setDetailsSheetOpen(false)} aria-label="Close">
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 deal-details-sheet-body">
                                    <DealFormRightSidebar {...sidebarProps} />
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}

